import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin } from "lucide-react";

/**
 * Embedded business location map. Lazily fetches cached lat/lng from the backend
 * (which calls OpenStreetMap Nominatim once and caches) so we don't burn an API
 * key. Falls back to a Google Maps "View on map" button if geocoding fails.
 */
export default function BusinessMap({ businessId, businessName, address }) {
  const [coords, setCoords] = useState(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    api.get(`/businesses/${businessId}/coords`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.ok && data.lat && data.lng) setCoords({ lat: data.lat, lng: data.lng });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTried(true);
      });
    return () => { cancelled = true; };
  }, [businessId]);

  if (!address && !coords) return null;

  // Build OpenStreetMap embed URL with a small bbox + marker around the coords.
  let embedSrc = null;
  if (coords) {
    const d = 0.004; // ~400m bounding box
    const minLng = coords.lng - d;
    const minLat = coords.lat - d;
    const maxLng = coords.lng + d;
    const maxLat = coords.lat + d;
    embedSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${minLng}%2C${minLat}%2C${maxLng}%2C${maxLat}&layer=mapnik&marker=${coords.lat}%2C${coords.lng}`;
  }

  const fallbackMapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${businessName || ""} ${address || ""}`.trim())}`;

  return (
    <div className="mt-8" data-testid="business-map-block">
      <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
        <MapPin size={12} /> Location
      </h3>
      <div className="mt-3 rounded-md border border-border overflow-hidden bg-gray-50" data-testid="business-map-host">
        {embedSrc ? (
          <iframe
            title={`Map showing ${businessName || "business"} location`}
            src={embedSrc}
            className="w-full h-72 block"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="business-map-iframe"
          />
        ) : (
          <div className="h-72 flex items-center justify-center text-sm text-gray-500 px-4 text-center">
            {tried ? "Map not available for this address yet." : "Loading map…"}
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-xs text-gray-500">
        <span>{address}</span>
        <a
          href={fallbackMapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#B31E1E] font-medium hover:underline"
          data-testid="business-map-open-link"
        >
          Open in Google Maps →
        </a>
      </div>
    </div>
  );
}
